/**
 * Centralized registry of every progress ID in the system.
 *
 * This is the single source of truth — both server and client import from here.
 * Progress IDs are a permanent user-data contract. Format: `<system>.<name>.v<N>`.
 * Bump `.vN` to force every user to re-see/re-do.
 *
 * Dynamic generation of IDs is intentionally avoided so that every key is
 * statically discoverable and grep-able.
 */
// ── Setup (permanent, never reset) ──────────────────────────────────────────
// These do not have version levels (v1). These are milestones that once complete are not undone unless explicitly unset. This allows us to update the onboarding events but not worry if we accidentally trigger onboarding again for someone that previously completed everything
export const SETUP_PROGRESS_IDS = {
    EMAIL_VERIFIED: "setup.emailVerified",
    ONBOARDING_COMPLETE: "setup.onboardingComplete", // Marked when all onboarding steps are complete
    /**
     * The user has seen the ATT primer — set on that page's Continue, read by `connectBanks.flow.ts`'s
     * `shouldSkip`. Permanent: the OS ATT dialog fires once per INSTALL, so re-showing the priming screen
     * would prime a dialog iOS will never present again. That's also why it must stay out of any reset
     * scope, and why bumping a `.vN` would be meaningless. (Its value reads `onboarding.*` — a shipped
     * value is a permanent contract, so it is not renamed to match the bucket.)
     *
     * ⚠ If anyone ever migrates this, COPY the old value; never blanket-mark users "seen".
     * `ATTPrimer.page.tsx` is the app's ONLY caller of `requestTrackingPermission()`, so a user who skips
     * that page never gets the ATT prompt at all — their attribution is lost.
     */
    ATT_PRIMER_SEEN: "onboarding.attPrimerSeen",
    /**
     * The user has seen the push-notifications primer — set on that page's Continue, read by
     * `onboarding.flow.ts`'s `shouldSkip`. Same shape and same reasoning as `ATT_PRIMER_SEEN`: the page
     * exists ONLY to prime the OS permission dialog, and that dialog surfaces at most once per install
     * (`requestPermissionsAsync` returns immediately with no UI once the user has decided). So on any
     * later pass — a Back from `Congrats`, a resume, a budget reset — the page is a dead screen priming
     * a prompt that will never appear. Permanent, and out of every reset scope, for that reason.
     *
     * ⚠ Being SEEN is not being GRANTED. Nothing may read this as "push is enabled" — the permission
     * itself is re-resolved by `runPostOnboardingTokenSync` on every login/foreground once onboarding
     * completes, which is also what covers the user who reaches this page on a second device.
     */
    NOTIFICATIONS_PRIMER_SEEN: "onboarding.notificationsPrimerSeen",
};
// ── Onboarding steps (resettable, versioned) ────────────────────────────────
// "Step" here means an onboarding-stage milestone shown to users ("Step 3 of
// 4"). Distinct from the flow-system `PageDef` (one page inside a flow) — the
// two concepts coexist in nearby code, so disambiguation matters. See
// projects/roo-app-mobile/CLAUDE.md § Progress, Gates, FlowRunner for the flow
// concept; this file owns the milestone concept.
export const ONBOARDING_STEP_PROGRESS_IDS = {
    STEP_1: "onboarding.step.1.v1",
    STEP_2: "onboarding.step.2.v1",
    STEP_3: "onboarding.step.3.v1",
    STEP_4: "onboarding.step.4.v1",
};
/** Ordered values for iteration (e.g. computing highest completed step). */
const ONBOARDING_STEP_VALUES = Object.values(ONBOARDING_STEP_PROGRESS_IDS);
/** Post-onboarding section — notifications + "how did you hear about us". */
export const ONBOARDING_POST_PROGRESS_ID = "onboarding.post.v1";
/** 1-indexed step (1..4) → progress id. */
export function onboardingStepProgressId(step) {
    const id = ONBOARDING_STEP_VALUES[step - 1];
    if (!id)
        throw new Error(`Invalid onboarding step: ${step}`);
    return id;
}
/** Total number of onboarding steps. */
export const ONBOARDING_STEP_COUNT = ONBOARDING_STEP_VALUES.length;
// ── Don't-show-again dismissals ─────────────────────────────────────────────
export const DONT_SHOW_AGAIN_PROGRESS_IDS = {
    incomeSplitterIncome: "dontShowAgain.incomeSplitter.income.v1",
    incomeSplitterPercentOfIncome: "dontShowAgain.incomeSplitter.percentOfIncome.v1",
    incomeSplitterBills: "dontShowAgain.incomeSplitter.bills.v1",
    incomeSplitterLiving: "dontShowAgain.incomeSplitter.living.v1",
    incomeSplitterDailyCash: "dontShowAgain.incomeSplitter.dailyCash.v1",
    incomeSplitterPayback: "dontShowAgain.incomeSplitter.payback.v1",
    incomeSplitterOtherDaily: "dontShowAgain.incomeSplitter.otherDaily.v1",
    incomeSplitterOtherMonthly: "dontShowAgain.incomeSplitter.otherMonthly.v1",
    incomeSplitterOverflow: "dontShowAgain.incomeSplitter.overflow.v1",
    /** When dismissed, the ViewAnticipatedDispersement modal opens directly on the
     *  Edit Split page, skipping the Income Splitter Rive summary preview. */
    incomeSplitterSummaryPage: "dontShowAgain.incomeSplitter.summaryPage.v1",
    highPositiveAmountNotSentToPlan: "dontShowAgain.highPositiveAmountNotSentToPlan.v1",
    /** Pre-connect coaching shown before the bank-connection widget on the
     *  new-connection add-bank path: "select ALL the accounts you want Roo to
     *  see". Dismissed via the prompt's "Don't show again" checkbox. Universal
     *  (not per-institution). */
    selectAllAccountsBeforeConnect: "dontShowAgain.selectAllAccountsBeforeConnect.v1",
};
// ── Course lessons ──────────────────────────────────────────────────────────
/**
 * Three id shapes live here, all hierarchical (deliberately NOT `.vN`-versioned
 * — see `flows/education/CLAUDE.md` § "Recipe: add a new course" step 3):
 *
 *   - `course.<c>.island.<i>.lesson.<l>` — the user finished that lesson.
 *   - `course.<c>.island.<i>.activated`  — the unlock ceremony revealed it.
 *   - `course.<c>.island.<i>.rewarded`   — the user earned the island's reward.
 *
 * The `rewarded` id is the **earned-at hedge**: island completion is otherwise
 * *derived* (a fold over lesson progress) and so carries no timestamp. Writing
 * one on first completion is what lets a future reward UI say *when* it was
 * earned — and it is written once and never re-written, so the original
 * timestamp survives. What was earned is named by `Island.reward` (a
 * `RewardId`, see `rewardIds.ts`).
 *
 * `lint:education` (`scripts/check-education-progress-ids.mjs`) enforces the
 * format, the activated↔rewarded pairing, and that nothing here is orphaned.
 */
export const COURSE_PROGRESS_IDS = {
    // exampleCourse > exampleIsland
    exampleCourse_exampleIsland_exampleLesson1: "course.exampleCourse.island.exampleIsland.lesson.exampleLesson1",
    exampleCourse_exampleIsland_exampleLesson2: "course.exampleCourse.island.exampleIsland.lesson.exampleLesson2",
    exampleCourse_exampleIsland_exampleLesson3: "course.exampleCourse.island.exampleIsland.lesson.exampleLesson3",
    exampleCourse_exampleIsland_hotspot: "course.exampleCourse.island.exampleIsland.lesson.hotspot",
    exampleCourse_exampleIsland_activated: "course.exampleCourse.island.exampleIsland.activated",
    exampleCourse_exampleIsland_rewarded: "course.exampleCourse.island.exampleIsland.rewarded",
    // roo_basics_1 > customizeBudgetPlan (replaced the old "basics" island, April-RooWay)
    roo_basics_1_customizeBudgetPlan_twoHalves: "course.roo_basics_1.island.customizeBudgetPlan.lesson.twoHalves",
    roo_basics_1_customizeBudgetPlan_unallocated: "course.roo_basics_1.island.customizeBudgetPlan.lesson.unallocated",
    roo_basics_1_customizeBudgetPlan_overAllocated: "course.roo_basics_1.island.customizeBudgetPlan.lesson.overAllocated",
    roo_basics_1_customizeBudgetPlan_percentOfIncome: "course.roo_basics_1.island.customizeBudgetPlan.lesson.percentOfIncome",
    roo_basics_1_customizeBudgetPlan_createPlanForExtraMoney: "course.roo_basics_1.island.customizeBudgetPlan.lesson.createPlanForExtraMoney",
    // roo_basics_1 > paychecksIncome
    roo_basics_1_paychecksIncome_activated: "course.roo_basics_1.island.paychecksIncome.activated",
    roo_basics_1_paychecksIncome_rewarded: "course.roo_basics_1.island.paychecksIncome.rewarded",
    roo_basics_1_paychecksIncome_incomeArrives: "course.roo_basics_1.island.paychecksIncome.lesson.incomeArrives",
    roo_basics_1_paychecksIncome_paycheckHistory: "course.roo_basics_1.island.paychecksIncome.lesson.paycheckHistory",
    roo_basics_1_paychecksIncome_extraIncome: "course.roo_basics_1.island.paychecksIncome.lesson.extraIncome",
    roo_basics_1_paychecksIncome_variableIncome: "course.roo_basics_1.island.paychecksIncome.lesson.variableIncome",
    roo_basics_1_paychecksIncome_incomeDeficit: "course.roo_basics_1.island.paychecksIncome.lesson.incomeDeficit",
    // roo_basics_1 > usingRoo
    roo_basics_1_usingRoo_activated: "course.roo_basics_1.island.usingRoo.activated",
    roo_basics_1_usingRoo_rewarded: "course.roo_basics_1.island.usingRoo.rewarded",
    roo_basics_1_usingRoo_spendToday: "course.roo_basics_1.island.usingRoo.lesson.spendToday",
    roo_basics_1_usingRoo_movingMoney: "course.roo_basics_1.island.usingRoo.lesson.movingMoney",
    roo_basics_1_usingRoo_match: "course.roo_basics_1.island.usingRoo.lesson.match",
    roo_basics_1_usingRoo_partnerBudgeting: "course.roo_basics_1.island.usingRoo.lesson.partnerBudgeting",
    roo_basics_1_usingRoo_reservoirLow: "course.roo_basics_1.island.usingRoo.lesson.reservoirLow",
    roo_basics_1_usingRoo_overspentPouch: "course.roo_basics_1.island.usingRoo.lesson.overspentPouch",
    roo_basics_1_usingRoo_netWorth: "course.roo_basics_1.island.usingRoo.lesson.netWorth",
    // roo_basics_1 > handlingDebt (relocated Debt lesson out of old "basics")
    roo_basics_1_handlingDebt_activated: "course.roo_basics_1.island.handlingDebt.activated",
    roo_basics_1_handlingDebt_rewarded: "course.roo_basics_1.island.handlingDebt.rewarded",
    roo_basics_1_handlingDebt_debt: "course.roo_basics_1.island.handlingDebt.lesson.debt",
    // roo_basics_1 > practice (hands-on cardSorter practice lessons). Renamed from an old "future"
    // placeholder island — its activation ID was allocated but never written, so the rename was safe.
    roo_basics_1_practice_activated: "course.roo_basics_1.island.practice.activated",
    roo_basics_1_practice_rewarded: "course.roo_basics_1.island.practice.rewarded",
    roo_basics_1_practice_oneNumber: "course.roo_basics_1.island.practice.lesson.oneNumber",
    roo_basics_1_practice_decide: "course.roo_basics_1.island.practice.lesson.decide",
    // ⚠ NEVER RE-MINT THE THREE BELOW — real users are writing progress against them.
    //
    // They ship in the `SwipingCards` island of the `PracticeActivities` course
    // (`PracticeActivities/SwipingCards/{payCreditCard,deleteCard,updatedTransaction}/`). They were
    // held back pending content sign-off for a long stretch, and this comment used to say so — which
    // made re-minting them look free, because nothing had been written yet. That is now backwards:
    // the lessons are live, so re-minting would silently un-complete them for everyone who has
    // already done them.
    //
    // ⚠ The id namespace deliberately does NOT match the tree. These keep their `roo_basics_1` /
    // `island.practice` naming after the catalog split precisely so existing completion records keep
    // resolving — identity is forever, location is not. Same rule as the `practice` island beside
    // them, which still carries `course.roo_basics_1.island.practice.*` inside `PracticeActivities`.
    // Read a mismatch here as history, never as a bug to tidy up.
    roo_basics_1_practice_payCreditCard: "course.roo_basics_1.island.practice.lesson.payCreditCard",
    roo_basics_1_practice_deleteCard: "course.roo_basics_1.island.practice.lesson.deleteCard",
    roo_basics_1_practice_updatedTransaction: "course.roo_basics_1.island.practice.lesson.updatedTransaction",
    // Minted by roo-content-builder.
    kevin_course_1_island1_activated: "course.kevin_course_1.island.island1.activated",
    // Minted by roo-content-builder.
    DebtPaybackPlan_gettingAheadOfYourDebt_activated: "course.DebtPaybackPlan.island.gettingAheadOfYourDebt.activated",
    // Minted by roo-content-builder.
    DebtPaybackPlan_meetThePaybackPouch_activated: "course.DebtPaybackPlan.island.meetThePaybackPouch.activated",
    // Minted by roo-content-builder.
    DebtPaybackPlan_PayingMoreThanTheMinimum_activated: "course.DebtPaybackPlan.island.PayingMoreThanTheMinimum.activated",
    // Minted by roo-content-builder.
    DebtPaybackPlan_yourDebtPaybackStrategy_activated: "course.DebtPaybackPlan.island.yourDebtPaybackStrategy.activated",
    // Minted by roo-content-builder.
    DebtPaybackPlan_WhyJustOnePaybackPouch_activated: "course.DebtPaybackPlan.island.WhyJustOnePaybackPouch.activated",
    // Minted by roo-content-builder.
    DebtPaybackPlan_PayDownFaster_activated: "course.DebtPaybackPlan.island.PayDownFaster.activated",
    // Minted by roo-content-builder.
    RooPsychology_DebtPlanPsychology_activated: "course.RooPsychology.island.DebtPlanPsychology.activated",
    // onboarding ("First Time Setup") > onboardingFlow
    // The step lessons (step1..step4) deliberately have NO dedicated lesson IDs —
    // they share the underlying onboarding milestones (ONBOARDING_STEP_PROGRESS_IDS)
    // so each lesson's check badge reflects the user's real onboarding state.
    // The Demo lesson has no progress ID at all — it's an action lesson that
    // always reads as complete (so it never gates its island), see
    // `Lesson.progressId?` in projects/roo-app-mobile/src/flows/education/types.ts.
    // Both islands moved here out of roo_basics_1 on 2026-09-21; the retired
    // roo_basics_1.island.onboarding ids were pruned with them.
    // Minted by roo-content-builder.
    onboarding_Demo_activated: "course.onboarding.island.Demo.activated",
    // Minted by roo-content-builder.
    customizeYourBudgetPlan_RooBudgetBasics_activated: "course.customizeYourBudgetPlan.island.RooBudgetBasics.activated",
    // Minted by roo-content-builder.
    PracticeActivities_SwipingCards_activated: "course.PracticeActivities.island.SwipingCards.activated",
    // These kept their ORIGINAL ids when roo_basics_1 was split, so nobody re-earns a badge they
    // already hold. The activated id travels with the rewarded one because lint:education pairs them
    // by prefix. onboardingFlow IS the old onboarding island and still awards its badge.
    //
    // ⚠ `roo_basics_1_customizeBudgetPlan_rewarded` is RETIRED, not orphaned: no island awards
    // `planCustomizer` until its lesson sequence is re-planned (see RETIRED_REWARDS in
    // scripts/check-education-progress-ids.mjs). Users already hold records in it, so KEEP it. When
    // the badge gets a new home, re-attach THIS id rather than minting one — a fresh id makes every
    // current holder earn it a second time — and move `_activated` onto that same island, or the
    // prefix pairing fails. RiverSectionsAndSetup carries `_activated` today only because it is the
    // half of the old island it was split from.
    roo_basics_1_onboarding_activated: "course.roo_basics_1.island.onboarding.activated",
    roo_basics_1_onboarding_rewarded: "course.roo_basics_1.island.onboarding.rewarded",
    roo_basics_1_customizeBudgetPlan_activated: "course.roo_basics_1.island.customizeBudgetPlan.activated",
    roo_basics_1_customizeBudgetPlan_rewarded: "course.roo_basics_1.island.customizeBudgetPlan.rewarded",
    // Minted by roo-content-builder.
    DebtPaybackPlan_WhenLifeHappens_activated: "course.DebtPaybackPlan.island.WhenLifeHappens.activated",
    // Minted by roo-content-builder.
    DebtPaybackPlan_DebtFreeNowWhat_activated: "course.DebtPaybackPlan.island.DebtFreeNowWhat.activated",
    // Minted by roo-content-builder.
    AccountBehaviors_AccountBehaviors_activated: "course.AccountBehaviors.island.AccountBehaviors.activated",
    AccountBehaviors_AccountBehaviors_accountBehaviors: "course.AccountBehaviors.island.AccountBehaviors.lesson.accountBehaviors",
    // Minted by roo-content-builder.
    DebtPaybackPlan_DebtFreeNowWhat_DebtFreeConfidenceCheck: "course.DebtPaybackPlan.island.DebtFreeNowWhat.lesson.DebtFreeConfidenceCheck",
    DebtPaybackPlan_DebtFreeNowWhat_WhenYouAreDebtFree: "course.DebtPaybackPlan.island.DebtFreeNowWhat.lesson.WhenYouAreDebtFree",
    DebtPaybackPlan_PayDownFaster_AddMoreFromLiving: "course.DebtPaybackPlan.island.PayDownFaster.lesson.AddMoreFromLiving",
    DebtPaybackPlan_PayDownFaster_PaydownFasterConfidenceCheck: "course.DebtPaybackPlan.island.PayDownFaster.lesson.PaydownFasterConfidenceCheck",
    DebtPaybackPlan_PayDownFaster_RollItForward: "course.DebtPaybackPlan.island.PayDownFaster.lesson.RollItForward",
    DebtPaybackPlan_PayingMoreThanTheMinimum_HelpRooAnswerPanda: "course.DebtPaybackPlan.island.PayingMoreThanTheMinimum.lesson.HelpRooAnswerPanda",
    DebtPaybackPlan_PayingMoreThanTheMinimum_MeetDebtPayback: "course.DebtPaybackPlan.island.PayingMoreThanTheMinimum.lesson.MeetDebtPayback",
    DebtPaybackPlan_PayingMoreThanTheMinimum_debtPaybackScreenFeatures: "course.DebtPaybackPlan.island.PayingMoreThanTheMinimum.lesson.debtPaybackScreenFeatures",
    DebtPaybackPlan_PayingMoreThanTheMinimum_payingMore_ConfidenceCheck: "course.DebtPaybackPlan.island.PayingMoreThanTheMinimum.lesson.payingMore_ConfidenceCheck",
    DebtPaybackPlan_WhenLifeHappens_AnUnexpectedCharge: "course.DebtPaybackPlan.island.WhenLifeHappens.lesson.AnUnexpectedCharge",
    DebtPaybackPlan_WhenLifeHappens_HelpPandaDecide: "course.DebtPaybackPlan.island.WhenLifeHappens.lesson.HelpPandaDecide",
    DebtPaybackPlan_WhenLifeHappens_IncreaseDebtConfidenceCheck: "course.DebtPaybackPlan.island.WhenLifeHappens.lesson.IncreaseDebtConfidenceCheck",
    DebtPaybackPlan_WhenLifeHappens_WhenToIncreaseDebt: "course.DebtPaybackPlan.island.WhenLifeHappens.lesson.WhenToIncreaseDebt",
    DebtPaybackPlan_WhyJustOnePaybackPouch_FillingThePaybackPouch: "course.DebtPaybackPlan.island.WhyJustOnePaybackPouch.lesson.FillingThePaybackPouch",
    DebtPaybackPlan_WhyJustOnePaybackPouch_OnePaybackPouchConfidenceCheck: "course.DebtPaybackPlan.island.WhyJustOnePaybackPouch.lesson.OnePaybackPouchConfidenceCheck",
    DebtPaybackPlan_WhyJustOnePaybackPouch_PowerOfOne: "course.DebtPaybackPlan.island.WhyJustOnePaybackPouch.lesson.PowerOfOne",
    DebtPaybackPlan_gettingAheadOfYourDebt_confreadyToGetAheadOfDebt: "course.DebtPaybackPlan.island.gettingAheadOfYourDebt.lesson.confreadyToGetAheadOfDebt",
    DebtPaybackPlan_gettingAheadOfYourDebt_isThisForYou: "course.DebtPaybackPlan.island.gettingAheadOfYourDebt.lesson.isThisForYou",
    DebtPaybackPlan_gettingAheadOfYourDebt_whyBalanceCostsMoney: "course.DebtPaybackPlan.island.gettingAheadOfYourDebt.lesson.whyBalanceCostsMoney",
    DebtPaybackPlan_gettingAheadOfYourDebt_whyThisMatters: "course.DebtPaybackPlan.island.gettingAheadOfYourDebt.lesson.whyThisMatters",
    DebtPaybackPlan_meetThePaybackPouch_PaybackPouchSetsMoneyAside: "course.DebtPaybackPlan.island.meetThePaybackPouch.lesson.PaybackPouchSetsMoneyAside",
    DebtPaybackPlan_meetThePaybackPouch_SwipeMatchingTransactions: "course.DebtPaybackPlan.island.meetThePaybackPouch.lesson.SwipeMatchingTransactions",
    DebtPaybackPlan_meetThePaybackPouch_WatchthePouchBalanceChange: "course.DebtPaybackPlan.island.meetThePaybackPouch.lesson.WatchthePouchBalanceChange",
    DebtPaybackPlan_meetThePaybackPouch_creditCardPaymentIsAMatch: "course.DebtPaybackPlan.island.meetThePaybackPouch.lesson.creditCardPaymentIsAMatch",
    DebtPaybackPlan_meetThePaybackPouch_meetPayback_ConfidenceCheck: "course.DebtPaybackPlan.island.meetThePaybackPouch.lesson.meetPayback_ConfidenceCheck",
    DebtPaybackPlan_yourDebtPaybackStrategy_DebtPaybackConfidenceCheck: "course.DebtPaybackPlan.island.yourDebtPaybackStrategy.lesson.DebtPaybackConfidenceCheck",
    DebtPaybackPlan_yourDebtPaybackStrategy_DebtPaybackStrategies: "course.DebtPaybackPlan.island.yourDebtPaybackStrategy.lesson.DebtPaybackStrategies",
    DebtPaybackPlan_yourDebtPaybackStrategy_PickAStrategyForPanda: "course.DebtPaybackPlan.island.yourDebtPaybackStrategy.lesson.PickAStrategyForPanda",
    DebtPaybackPlan_yourDebtPaybackStrategy_addingAndRemovingDebts: "course.DebtPaybackPlan.island.yourDebtPaybackStrategy.lesson.addingAndRemovingDebts",
    RooPsychology_DebtPlanPsychology_PsychologyOfOnePaybackPouch: "course.RooPsychology.island.DebtPlanPsychology.lesson.PsychologyOfOnePaybackPouch",
    RooPsychology_DebtPlanPsychology_ThePsychologyOfOnePaybackPouch: "course.RooPsychology.island.DebtPlanPsychology.lesson.ThePsychologyOfOnePaybackPouch",
    kevin_course_1_island1_lesson1: "course.kevin_course_1.island.island1.lesson.lesson1",
    kevin_course_1_island1_lesson2: "course.kevin_course_1.island.island1.lesson.lesson2",
};
// ── Feature gates ──────────────────────────────────────────────────────────
/**
 * Stable IDs for in-app features that can be gated behind one or more
 * lessons. The KEY observation: features ("the user can add a
 * percent-of-income section") are forever; lessons are content that evolves.
 * Gating directly on a lesson's progressId means a lesson slug rename would
 * re-lock the feature for existing users.
 *
 * Instead:
 *   - Features get stable IDs here. They never change, never get versioned,
 *     never get bumped — same convention as `SETUP_PROGRESS_IDS`.
 *   - Lessons declare which features they unlock via `Lesson.unlocks`.
 *   - Completing a lesson marks both the lesson's progressId AND every
 *     feature in its `unlocks` array.
 *   - UI gates (`<LockedUntil featureGateId>`, `s_gate.checkFeatureGate`)
 *     key on the feature, never on the lesson.
 *
 * When you split a lesson, rename its slug, or replace it with new content,
 * the feature gate keeps its progressId — existing users stay unlocked. The
 * `flows/education/featureGateRegistry.ts` map tells the gate which lesson
 * to navigate to when prompting; updating that mapping is the only thing
 * required for a lesson refactor.
 */
export const FEATURE_GATE_IDS = {
    addPercentOfIncome: "feature.addPercentOfIncome",
    addOverflowOfIncome: "feature.addOverflowOfIncome",
    addPaybackPlan: "feature.addPaybackPlan",
    fundFromPouch: "feature.fundFromPouch",
    useMatch: "feature.useMatch",
    useNetWorth: "feature.useNetWorth",
};
// ── Roo Way tour ───────────────────────────────────────────────────────────
/**
 * First-visit overlays on the Roo Way tab. `welcome` gates a full-screen
 * blurred welcome card the first time a user lands on the tab; tapping
 * GET STARTED marks it complete and the overlay unmounts.
 *
 * In demo mode the overlay always renders (regardless of progress) and the
 * GET STARTED button is `demoPolicy: "block"`ed — see `RooWayWelcomeOverlay`.
 */
export const WAY_TOUR_PROGRESS_IDS = {
    welcome: "wayTour.welcome.v1",
};
// ── Income splitter tour ───────────────────────────────────────────────────
/**
 * Demo-tour education on the Income Splitter (Plan) screen. `goalModeTip` drives an
 * `InlineBananaTip` — an education card the user actively DISMISSES, so it follows them across
 * devices (the same call `DONT_SHOW_AGAIN_PROGRESS_IDS` makes).
 *
 * ⚠ The passive halos on this screen are NOT here — they live in mobile's device-local `s_hints`.
 * Classify a new id by its CONSUMER (a halo is a fact about a screen on a device; a dismissal is a
 * fact about a person), not by which screen it sits on.
 */
export const INCOME_SPLITTER_TOUR_PROGRESS_IDS = {
    goalModeTip: "incomeSplitterTour.goalModeTip.v1",
};
// ── Demo / debug gates ─────────────────────────────────────────────────────
export const DEMO_GATE_PROGRESS_IDS = {
    /** Eligibility IDs — simulate completing training / onboarding. */
    eligibility1: "demo.gate.eligibility1.v1",
    eligibility2: "demo.gate.eligibility2.v1",
};
// ── Watched locked videos ──────────────────────────────────────────────────
/**
 * "This user watched the locked video on page X, all the way through."
 *
 * Only pages that opt in to `playback: "locked"` need an entry — a normal video page gates nothing
 * and stores nothing. One id per opted-in page, added by hand.
 *
 * **Keyed by PAGE, never by video.** The id names the page's role, not its content, so re-recording
 * the video, swapping its `VideoKey`, or pushing a `GlobalSettings.videoOverrides` entry never asks a
 * user to sit through it again. That decoupling is the whole reason this bucket exists rather than
 * keying on `VideoKey`.
 *
 * ⚠ **Enumerate every id. Never template, derive, or prefix-match one.** A locked page cannot be
 * exited, so its unlock state is the difference between a user continuing and a user reinstalling —
 * it has to be greppable, and it has to land in `ALL_PROGRESS_IDS` so the e2e `skipAllProgress`
 * helper releases it like every other gate. (File header rule + ADR: progress-id-classification,
 * which rejected prefix rules outright.)
 *
 * Why these are progress ids at all: the classification table's strict test is "something outside the
 * flow reads it", and by that test alone this would be page state. It qualifies under the ADR's named
 * reconsider trigger — a device-local hint that must become authoritative across devices is a progress
 * id. A user who watched on their phone must not re-watch on their tablet.
 */
export const VIDEO_WATCHED_PROGRESS_IDS = {
    /**
     * The "Welcome to Roo Money" video — onboarding's first video (`onboarding.flow.ts`). Minted when it
     * was the create-account flow's last page; the VALUE did not change when the page moved to the front
     * of onboarding, so nobody who watched it at signup is asked to watch it again.
     */
    welcomeToRoo: "video.welcomeToRoo.v1",
    // Onboarding step 1's overview, right after the roadmap — three watch-to-unlock pages in a row
    // (`onboarding.flow.ts`). One id per PAGE, so each part gates on its own: sharing a single id
    // would unlock parts 2 and 3 the moment part 1 finished, letting a returning user walk past two
    // videos they never watched.
    //
    // ⚠ Part 1's VALUE is deliberately still `video.rooOverview.v1` — the page predates the split and
    // only swapped its `VideoKey`, which must never ask an existing watcher to sit through it again.
    // The key was renamed for symmetry with its siblings; the wire value is the contract, not the key.
    rooOverviewPart1: "video.rooOverview.v1",
    rooOverviewPart2: "video.rooOverviewPart2.v1",
    // ⚠ Its OWN id, deliberately not shared with `rooOverviewPart2` — the two are siblings ("Identify
    // Income" then "Identify Bills") but they are two PAGES, and one id across both would unlock the
    // bills video the moment the income one finished. Same rule, and same reason, as parts 1-3 above.
    // The key reads `2b` because the page does; the wire value is what users are recorded against.
    rooOverviewPart2bBills: "video.rooOverviewPart2bBills.v1",
    rooOverviewPart3: "video.rooOverviewPart3.v2",
    rooOverviewPart4: "video.rooOverviewPart4.v1",
    rooOverviewPart5: "video.rooOverviewPart5.v1",
    threeFundingMethods: "video.threeFundingMethods.v1",
    billsVsLiving: "video.billsVsLiving.v1",
    pouchTransfers: "video.pouchTransfers.v1",
    // Onboarding step 3's six teaching videos — all watch-to-unlock, all auto-advancing. Declared
    // per PAGE, never per video: re-recording a video or swapping its `VideoKey` must never ask
    // anyone to watch it again.
    incomeNeedsRiverbed: "video.incomeNeedsRiverbed.v1",
    roosThreeAllocationTypes: "video.roosThreeAllocationTypes.v1",
    meetYourReservoir: "video.meetYourReservoir.v1",
    neverOverspendAgain: "video.neverOverspendAgain.v1",
    theTrick: "video.theTrick.v2",
    whatToPullOutOfLiving: "video.whatToPullOutOfLiving.v1",
    // Onboarding step 4's three videos. `whyCreditCardDebtStopsHere` is credit-card holders only.
    whyPouchesMatchBank: "video.whyPouchesMatchBank.v1",
    whyCreditCardDebtStopsHere: "video.whyCreditCardDebtStopsHere.v1",
    whatsNext: "video.whatsNext.v1",
};
/** Flat array of every registered progress ID. Used by e2e helpers to mark all progress as complete. */
export const ALL_PROGRESS_IDS = [
    ...Object.values(SETUP_PROGRESS_IDS),
    ...Object.values(ONBOARDING_STEP_PROGRESS_IDS),
    ONBOARDING_POST_PROGRESS_ID,
    ...Object.values(DONT_SHOW_AGAIN_PROGRESS_IDS),
    ...Object.values(COURSE_PROGRESS_IDS),
    ...Object.values(FEATURE_GATE_IDS),
    ...Object.values(INCOME_SPLITTER_TOUR_PROGRESS_IDS),
    ...Object.values(WAY_TOUR_PROGRESS_IDS),
    ...Object.values(DEMO_GATE_PROGRESS_IDS),
    ...Object.values(VIDEO_WATCHED_PROGRESS_IDS),
];
/**
 * Onboarding-scope subset — cleared by the hard reset path so the user is
 * dropped back into onboarding step 1 with a fresh slate. Includes:
 *   - All four onboarding step milestones
 *   - Post-onboarding section milestone (notifications + "how did you hear")
 *   - The `SETUP_PROGRESS_IDS.ONBOARDING_COMPLETE` milestone (so the gate at
 *     Settings → App Details → Demo Mode CTA re-closes)
 *
 * ⚠ It clears exactly ONE `SETUP_PROGRESS_IDS` member, by name — never the
 * whole bucket. `EMAIL_VERIFIED`, `ATT_PRIMER_SEEN` and
 * `NOTIFICATIONS_PRIMER_SEEN` are permanent facts about the person and their
 * install; a budget reset must not re-arm any of them.
 *
 * Deliberately EXCLUDES tutorials, guided tours, `dontShowAgain.*`, widget
 * tooltip dismissals, course/lesson progress, and watched locked videos — those
 * are educational dismissals the user shouldn't be punished for after a budget
 * reset. (A locked video is the sharpest case: re-arming one would strand a
 * resetting user on a page they cannot exit until they re-watch it.)
 *
 * Pinned by `tests/onboardingScopeProgressIds.test.ts` so a new
 * onboarding-related bucket doesn't silently drift the aggregate.
 */
export const ONBOARDING_SCOPE_PROGRESS_IDS = [
    ...Object.values(ONBOARDING_STEP_PROGRESS_IDS),
    ONBOARDING_POST_PROGRESS_ID,
    SETUP_PROGRESS_IDS.ONBOARDING_COMPLETE,
];
/**
 * Progress ids cleared by the Partial Reset path (Settings → Reset Budget →
 * Partial Reset) so the user re-walks onboarding step 4 with the overview
 * Setup Guide visible as the re-entry affordance.
 *
 * BOTH ids must be cleared together. Unmarking only `STEP_4` leaves
 * `setup.onboardingComplete` set, which hides the Setup Guide
 * (`SetupGuideRow.comp.tsx`) while the step-4 locks stay active
 * (`gate.service`) — a user who abandons the re-walk is then stranded with no
 * in-app path back into step 4. Clearing `ONBOARDING_COMPLETE` too makes
 * `resolvePostAuth` land them on overview with the guide showing step 4 as the
 * next actionable step. This is the partial-reset analogue of the hard-reset
 * `ONBOARDING_SCOPE_PROGRESS_IDS`, which already clears `ONBOARDING_COMPLETE`.
 *
 * Pinned by `tests/partialResetProgressIds.test.ts`.
 */
export const PARTIAL_RESET_PROGRESS_IDS = [
    ONBOARDING_STEP_PROGRESS_IDS.STEP_4,
    SETUP_PROGRESS_IDS.ONBOARDING_COMPLETE,
];
//# sourceMappingURL=progressIds.js.map